import {
  ComplaintReason, IntegratedSystem, OccurrenceNature, OccurrenceOrigin, OccurrenceOriginDetail,
  OccurrenceScope, OccurrenceSubScope, OccurrenceType, ReportCategory,
} from './occurrence.models';

/**
 * Catálogo da cascata Âmbito → Subâmbito → Tipo → Natureza → Origem → Detalhe da origem.
 * Espelha OccurrenceCatalog.java (fonte de verdade da validação no backend) — manter em sincronia.
 */

/** Subâmbitos válidos por âmbito ("Outro" e Segurança da Informação ainda não têm). */
export const SCOPE_SUB_SCOPES: Record<OccurrenceScope, readonly OccurrenceSubScope[]> = {
  SISTEMAS_GESTAO: ['SISTEMA_INTEGRADO', 'ISO_9001', 'ISO_56001', 'NP_4590', 'OUTRO_SISTEMA'],
  RGPD: ['EXERCICIO_DIREITOS', 'CONFORMIDADE_PROTECAO_DADOS'],
  RGPC: ['GESTAO_COMUNICACOES', 'CUMPRIMENTO_NORMATIVO'],
  SEGURANCA_INFORMACAO: [],
  OUTRO: [],
};

export const INTEGRATED_SYSTEMS: readonly IntegratedSystem[] = ['ISO_9001', 'ISO_56001', 'NP_4590', 'OUTRO_SISTEMA'];

/**
 * A lista geral do documento de 2026-10-05: vale em qualquer âmbito e subâmbito, menos nas duas
 * exceções abaixo. Antes havia uma lista por combinação e as diferenças não tinham regra.
 */
const GENERAL_TYPES: readonly OccurrenceType[] = [
  'NAO_CONFORMIDADE', 'OPORTUNIDADE_MELHORIA', 'ALTERACAO_SISTEMA', 'ACAO_CORRETIVA',
  'PARTICIPACAO_COMUNICACAO', 'OUTRO',
];

/** Primeira exceção: o pedido de exercício de direitos tem os tipos do RGPD. */
const RGPD_RIGHTS: readonly OccurrenceType[] = [
  'ACESSO', 'RETIFICACAO', 'APAGAMENTO', 'LIMITACAO_TRATAMENTO', 'PORTABILIDADE', 'OPOSICAO',
  'RETIRADA_CONSENTIMENTO', 'NAO_SUJEICAO_DECISAO_AUTOMATIZADA', 'OUTRO',
];

/** Segunda exceção: a gestão de comunicações é só participação (ou outro). */
const RGPC_COMMUNICATIONS: readonly OccurrenceType[] = ['PARTICIPACAO_COMUNICACAO', 'OUTRO'];

/**
 * Terceira exceção: a gestão do cumprimento normativo não recebe participações — essas entram
 * pela gestão de comunicações (revisão de 2026-10-19).
 */
const RGPC_COMPLIANCE: readonly OccurrenceType[] =
  GENERAL_TYPES.filter((type) => type !== 'PARTICIPACAO_COMUNICACAO');

/** Tipos válidos para a combinação âmbito/subâmbito. */
export function typesFor(
  scope: OccurrenceScope | null,
  subScope: OccurrenceSubScope | null,
): readonly OccurrenceType[] {
  if (scope === 'OUTRO' || scope === 'SEGURANCA_INFORMACAO') return GENERAL_TYPES;
  if (!scope || !subScope) return [];
  if (subScope === 'EXERCICIO_DIREITOS') return RGPD_RIGHTS;
  if (subScope === 'GESTAO_COMUNICACOES') return RGPC_COMMUNICATIONS;
  if (subScope === 'CUMPRIMENTO_NORMATIVO') return RGPC_COMPLIANCE;
  return GENERAL_TYPES;
}

/**
 * Todos os tipos que podem ocorrer num âmbito (união dos subâmbitos) e, sem âmbito escolhido, a
 * união de todos os âmbitos. Usado pelo filtro da lista, que antes despejava todos os tipos numa
 * lista plana enquanto o formulário usava a cascata. Cada tipo aparece uma só vez, pela ordem do
 * catálogo e com "Outro" no fim.
 */
export function typesForScope(scope: OccurrenceScope | null): readonly OccurrenceType[] {
  const scopes = scope ? [scope] : (Object.keys(SCOPE_SUB_SCOPES) as OccurrenceScope[]);
  const union = new Set<OccurrenceType>();
  for (const current of scopes) {
    const subScopes = SCOPE_SUB_SCOPES[current];
    if (!subScopes.length) {
      for (const type of typesFor(current, null)) union.add(type);
      continue;
    }
    for (const subScope of subScopes) {
      for (const type of typesFor(current, subScope)) union.add(type);
    }
  }
  union.delete('OUTRO');
  return [...union, 'OUTRO'];
}

/**
 * Naturezas da Participação/Comunicação: a reclamação só existe nos sistemas de gestão.
 *
 * Sugestões/Ideias (uma só natureza desde 2026-10-01) seguem as duas fontes do documento: dentro
 * dos sistemas de gestão aplicam-se só à ISO 56001 — direta ou dentro do sistema integrado — e
 * fora deles a folha dos campos do canal mostra-as no âmbito Outro, que é por onde o site as
 * encaminha.
 */
export function naturesFor(
  scope: OccurrenceScope | null,
  subScope: OccurrenceSubScope | null,
  systems: readonly IntegratedSystem[],
): readonly OccurrenceNature[] {
  const natures: OccurrenceNature[] = [];
  if (complaintsAllowed(scope, subScope, systems)) natures.push('RECLAMACAO');
  if (reportsAllowed(scope, subScope, systems)) natures.push('DENUNCIA');
  const innovation = subScope === 'ISO_56001'
    || (subScope === 'SISTEMA_INTEGRADO' && systems.includes('ISO_56001'));
  if (scope !== 'SISTEMAS_GESTAO' || innovation) natures.push('SUGESTAO_IDEIA');
  natures.push('ELOGIO_RECONHECIMENTO');
  return natures;
}

/**
 * Onde há reclamações: só nos sistemas de gestão, e dentro deles só nos subâmbitos que têm
 * motivos de reclamação — a ISO 9001 (produto/serviço) e a NP 4590 (bem-estar). O sistema
 * integrado herda-as de um desses dois.
 *
 * <p>Até 2026-10-08 bastava o âmbito: trocar o subâmbito para a ISO 56001 deixava lá ficar uma
 * reclamação que aquele subâmbito não conhece, e o formulário seguia calado.
 */
export function complaintsAllowed(
  scope: OccurrenceScope | null,
  subScope: OccurrenceSubScope | null,
  systems: readonly IntegratedSystem[],
): boolean {
  if (scope !== 'SISTEMAS_GESTAO') return false;
  if (subScope === 'ISO_9001' || subScope === 'NP_4590') return true;
  if (subScope === 'SISTEMA_INTEGRADO') {
    return systems.includes('ISO_9001') || systems.includes('NP_4590');
  }
  // Sem subâmbito escolhido ainda não há razão para esconder a natureza.
  return subScope === null;
}

/**
 * Onde há denúncias: em todos os âmbitos, menos dentro dos sistemas de gestão, onde só a NP 4590
 * as conhece — "Só há denuncia para o subâmbito NP 4590" (documento de 2026-10-08). É a norma do
 * bem-estar, e é dela a lista de naturezas do incidente que a denúncia aí oferece.
 */
export function reportsAllowed(
  scope: OccurrenceScope | null,
  subScope: OccurrenceSubScope | null,
  systems: readonly IntegratedSystem[],
): boolean {
  if (scope !== 'SISTEMAS_GESTAO') return true;
  if (subScope === null) return true;
  if (subScope === 'NP_4590') return true;
  return subScope === 'SISTEMA_INTEGRADO' && systems.includes('NP_4590');
}

/**
 * Motivos da reclamação por subâmbito: o bem-estar é da NP 4590 e o produto/serviço da ISO 9001
 * (folha dos campos do canal, 2026-10-01).
 */
export function complaintReasonsFor(
  subScope: OccurrenceSubScope | null,
): readonly ComplaintReason[] {
  if (subScope === 'NP_4590') return ['CONDICOES_TRABALHO_BEM_ESTAR', 'OUTRO'];
  if (subScope === 'ISO_9001') return ['PRODUTO_SERVICO', 'ATENDIMENTO_COMUNICACAO', 'OUTRO'];
  return ['PRODUTO_SERVICO', 'ATENDIMENTO_COMUNICACAO', 'CONDICOES_TRABALHO_BEM_ESTAR', 'OUTRO'];
}

/** Categorias da denúncia no RGPD — lista própria desde o documento de 2026-10-01. */
const RGPD_CATEGORIES: readonly ReportCategory[] = [
  'ACESSO_UTILIZACAO_INDEVIDA_DADOS', 'DIVULGACAO_PARTILHA_NAO_AUTORIZADA',
  'FALHA_SEGURANCA_CONFIDENCIALIDADE', 'OUTRA_IRREGULARIDADE',
];

/** Categorias da denúncia nos restantes âmbitos — a lista do RGPC. */
const RGPC_CATEGORIES: readonly ReportCategory[] = [
  'CORRUPCAO_FRAUDE', 'CONFLITO_INTERESSES', 'VIOLACAO_CODIGO_CONDUTA', 'ASSEDIO_DISCRIMINACAO',
  'VIOLACAO_LEGAL_REGULAMENTAR', 'BRANQUEAMENTO_CAPITAIS', 'OUTRA_IRREGULARIDADE',
];

/**
 * Nos sistemas de gestão só há denúncia no subâmbito NP 4590, e aí a natureza é do bem-estar
 * (documento de 2026-10-08).
 */
const MANAGEMENT_CATEGORIES: readonly ReportCategory[] = [
  'BEM_ESTAR_ORGANIZACIONAL', 'CARGA_TRABALHO', 'SEGURANCA_SAUDE', 'OUTRA_IRREGULARIDADE',
];

/**
 * A natureza do incidente ou violação, por âmbito.
 *
 * <p>Era uma lista só — catorze entradas que misturavam o bem-estar da NP 4590 com o
 * branqueamento de capitais do RGPC — e cada âmbito via tudo. Desde 2026-10-08 cada âmbito tem
 * a sua. A da Segurança da Informação não vive aqui: são as naturezas do incidente
 * ({@link IncidentNature}), o campo que já existia para elas.
 */
export function reportCategoriesFor(scope: OccurrenceScope | null): readonly ReportCategory[] {
  if (scope === 'RGPD') return RGPD_CATEGORIES;
  if (scope === 'SISTEMAS_GESTAO') return MANAGEMENT_CATEGORIES;
  return RGPC_CATEGORIES;
}

/**
 * As origens do documento de 2026-10-05: uma lista só, igual para todos os tipos.
 *
 * A ordem dos campos mudou — a origem escolhe-se antes do tipo —, por isso já não pode depender
 * dele. A reunião de gestão entra à parte, só nos sistemas de gestão, e a "outra origem" fica no
 * fim, como em todas as listas desta casa.
 */
const GENERAL_ORIGINS: readonly OccurrenceOrigin[] = [
  'SITE', 'ESSENTIALS', 'EMAIL', 'REUNIAO', 'AUDITORIA', 'AVALIACAO_MONITORIZACAO',
  // A ocorrência que nasce de uma linha de seguimento de outra (documento de 2026-10-08).
  'DECORRENTE_OUTRA_OCORRENCIA', 'OUTRA_ORIGEM',
];

/**
 * Na gestão de comunicações do RGPC não há auditorias nem monitorização: o que lá chega é
 * comunicado por alguém (revisão de 2026-10-19).
 */
const NOT_IN_RGPC_COMMUNICATIONS: readonly OccurrenceOrigin[] = ['AUDITORIA', 'AVALIACAO_MONITORIZACAO'];

/** Origens oferecidas no âmbito e subâmbito. */
export function originsFor(
  scope: OccurrenceScope | null,
  subScope: OccurrenceSubScope | null = null,
): readonly OccurrenceOrigin[] {
  const origins: OccurrenceOrigin[] = [];
  for (const origin of GENERAL_ORIGINS) {
    if (subScope === 'GESTAO_COMUNICACOES' && NOT_IN_RGPC_COMMUNICATIONS.includes(origin)) continue;
    if (origin === 'AUDITORIA' && scope === 'SISTEMAS_GESTAO') origins.push('REUNIAO_GESTAO');
    origins.push(origin);
  }
  return origins;
}

/** Não conformidades, ações corretivas e oportunidades têm árvore de origem própria. */
function hasOwnOriginTree(type: OccurrenceType | null): boolean {
  return type === 'NAO_CONFORMIDADE' || type === 'ACAO_CORRETIVA' || type === 'OPORTUNIDADE_MELHORIA';
}

/**
 * Detalhes de origem válidos para a categoria, condicionados ao tipo e ao âmbito/subâmbito
 * (DPIA/RAT só RGPD, PPR só RGPC, CNPD/MENAC idem; fornecedores/satisfação de clientes com
 * ISO 9001; satisfação do colaborador com NP 4590). Vazio = categoria sem detalhe.
 * Espelha OccurrenceCatalog.originDetailsFor no backend — manter em sincronia.
 */
export function originDetailsFor(
  origin: OccurrenceOrigin | null,
  scope: OccurrenceScope | null,
  subScope: OccurrenceSubScope | null,
  systems: readonly IntegratedSystem[],
  type: OccurrenceType | null,
): readonly OccurrenceOriginDetail[] {
  const hasSystem = (direct: OccurrenceSubScope, integrated: IntegratedSystem) =>
    subScope === direct || (subScope === 'SISTEMA_INTEGRADO' && systems.includes(integrated));
  switch (origin) {
    // A reunião deixou de ter detalhe: a de gestão passou a origem própria em 2026-10-05, e
    // distinguir "de equipa" de "outra" não mudava nada no tratamento do caso.
    case 'COLABORADOR':
      return ['COLABORADOR_INTERNO', 'CONSULTOR_CPS', 'CONSULTOR_CST_CTI'];
    case 'AUDITORIA':
      return hasOwnOriginTree(type)
        ? ['AUDITORIA_INTERNA', 'AUDITORIA_EXTERNA']
        : ['AUDITORIA_INTERNA', 'AUDITORIA_EXTERNA', 'OUTRA_AUDITORIA'];
    case 'AUTORIDADE_EXTERNA': {
      const details: OccurrenceOriginDetail[] = [];
      if (scope === 'RGPD') details.push('CNPD');
      if (scope === 'RGPC') details.push('MENAC');
      details.push('OUTRA_AUTORIDADE');
      return details;
    }
    case 'AVALIACAO_MONITORIZACAO': {
      if (hasOwnOriginTree(type)) {
        const details: OccurrenceOriginDetail[] = ['RISCO', 'OPORTUNIDADE', 'OBJETIVOS_INDICADORES'];
        if (hasSystem('ISO_9001', 'ISO_9001')) details.push('AVALIACAO_FORNECEDORES');
        details.push('ACOMPANHAMENTO_ACOES_ANTERIORES', 'SATISFACAO');
        // Fora do modelo GBT, mas é por aqui que nasce a ocorrência derivada — e uma ação
        // corretiva responde quase sempre a outra ocorrência.
        details.push('ACAO_RESPOSTA_OCORRENCIA');
        return details;
      }
      const details: OccurrenceOriginDetail[] =
        ['MONITORIZACAO_SISTEMA', 'ACAO_RESPOSTA_OCORRENCIA', 'AVALIACAO_EFICACIA'];
      if (hasSystem('ISO_9001', 'ISO_9001')) {
        details.push('AVALIACAO_FORNECEDORES', 'AVALIACAO_SATISFACAO_CLIENTES');
      }
      if (hasSystem('NP_4590', 'NP_4590')) details.push('AVALIACAO_SATISFACAO_COLABORADOR');
      if (scope === 'RGPD') details.push('DPIA', 'REVISAO_RAT');
      if (scope === 'RGPC') details.push('REVISAO_PPR');
      return details;
    }
    default:
      return [];
  }
}
